import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Search, Loader, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

// Hook לDebouncing
const useDebounce = (callback, delay) => {
  const timeoutRef = useRef(null);

  return useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);
};

const OptimizedSearchAutocomplete = ({ 
  selectedCategory = null, 
  onSearchSubmit, 
  placeholder = 'חפש בכל מוצרי החיסכון...',
  loading = false 
}) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  
  const searchRef = useRef(null);
  const suggestionsRef = useRef(null);
  const lastRequestRef = useRef(null); // לביטול בקשות ישנות

  // פונקציה לקביעת קטגוריה של מוצר לניווט
  const getProductCategory = (product) => {
    const source = product.source || product._source;
    switch (source) {
      case 'gamal-net':
        if (product.name?.includes('השתלמות')) return 'study-funds';
        if (product.name?.includes('ילד')) return 'child-savings';
        if (product.classification?.includes('השקעה')) return 'investment-funds';
        return 'pension-savings';
      case 'pension-net': return 'pension-plans';
      case 'insurance-net': return 'insurance-savings';
      default: return selectedCategory || 'study-funds';
    }
  };
const getProductType = (product) => {
  const source = product.source || product._source;
  const name = product.name || '';
  const classification = product.classification || '';

  switch (source) {
    case 'gamal-net':
      if (name.includes('השתלמות')) return 'קרן השתלמות';
      if (name.includes('ילד')) return 'חסכון לילד';
      if (classification.includes('השקעה')) return 'קופת גמל להשקעה';
      return 'קופת גמל';
    case 'pension-net': 
      return 'קרן פנסיה';
    case 'insurance-net': 
      return ' פוליסת חיסכון';
    default: 
      return '';
  }
};
  // 🚀 Debounced search - מחכה 300ms אחרי הפסקת הקלדה
  const debouncedSearch = useDebounce(async (searchTerm) => {
    if (searchTerm.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      setLoadingSuggestions(false);
      return;
    }

    const requestId = Date.now();
    lastRequestRef.current = requestId;

    setLoadingSuggestions(true);
    
    try {
   
      
      const startTime = performance.now();
      const results = await api.searchProducts(searchTerm, {
        category: selectedCategory,
        limit:100 // רק 100 הצעות לAutocomplete
      });

      // בדיקה שזה עדיין הבקשה הרלוונטיות (לא הוחלפה בזמן ההמתנה)
      if (lastRequestRef.current !== requestId) {
      
        return;
      }

      const duration = Math.round(performance.now() - startTime);


      setSuggestions(results.slice(0, 400));
      setShowSuggestions(true);
      
    } catch (error) {
      console.error('❌ Error fetching suggestions:', error);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      // רק אם זה עדיין הבקשה הרלוונטית
      if (lastRequestRef.current === requestId) {
        setLoadingSuggestions(false);
      }
    }
  }, 300); // 300ms debounce

  // טיפול בשינוי טקסט החיפוש
  const handleSearchTermChange = (newTerm) => {
    setSearchTerm(newTerm);
    setSelectedIndex(-1);
    
    // אם נמחק הטקסט - הסתר הצעות מיד
    if (!newTerm.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      setLoadingSuggestions(false);
      return;
    }

    // הפעלת debounced search
    debouncedSearch(newTerm);
  };

  // טיפול במקלדת
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit(e);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : -1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev > -1 ? prev - 1 : suggestions.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && suggestions[selectedIndex]) {
          selectSuggestion(suggestions[selectedIndex]);
        } else {
          handleSubmit(e);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  // בחירת הצעה - ניווט ישיר לדף המוצר
  const selectSuggestion = (suggestion) => {
  
    // קביעת קטגוריה מתאימה לניווט
    const productCategory = selectedCategory || getProductCategory(suggestion);
    
    // ניווט ישיר לדף המוצר
    navigate(`/product/${productCategory}/${suggestion.id}`);
    
    // ניקוי החיפוש
    setSearchTerm('');
    setShowSuggestions(false);
    setSelectedIndex(-1);
  };

  // שליחת החיפוש הכללי
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!searchTerm.trim()) return;

    setShowSuggestions(false);
    setSelectedIndex(-1);



    try {
      const startTime = performance.now();
      const results = await api.searchProducts(searchTerm, {
        category: selectedCategory,
        limit: 200 // יותר תוצאות לחיפוש מלא
      });

      const duration = Math.round(performance.now() - startTime);
     
      onSearchSubmit(results, searchTerm);
      
    } catch (error) {
      console.error('❌ Search error:', error);
      onSearchSubmit([], searchTerm, error.message);
    }
  };

  // ניקוי החיפוש
  const clearSearch = () => {
    setSearchTerm('');
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    setLoadingSuggestions(false);
    lastRequestRef.current = null;
  };

  // סגירת הצעות בלחיצה מחוץ לקומפוננט
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchRef.current && 
        !searchRef.current.contains(event.target) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ניקוי בשינוי קטגוריה
  useEffect(() => {
    clearSearch();
  }, [selectedCategory]);

  // עדכון מיקום הרשימה כאשר היא מוצגת
  useEffect(() => {
    if (!showSuggestions || !searchRef.current) return;

    const updatePosition = () => {
      const rect = searchRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        left: rect.left,
        width: rect.width
      });
    };

    // עדכון ראשוני
    updatePosition();

    // עדכון בעת scroll או resize
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showSuggestions]);

  return (
    <div ref={searchRef} className="relative w-full max-w-3xl mx-auto" style={{ zIndex: 999999 }}>
      {/* שורת החיפוש - מותאמת למובייל */}
      <form onSubmit={handleSubmit} className="relative group">
        {/* רקע הולוגרמי מתקדם */}
        <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500/20 via-blue-500/20 to-purple-500/20 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
        
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => handleSearchTermChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="relative w-full px-3 py-2 md:px-6 md:py-3 text-sm md:text-base text-gray-900 bg-gradient-to-br from-white to-gray-50 rounded-2xl border border-gray-300/50 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 shadow-lg md:shadow-xl pr-10 md:pr-14 placeholder:text-gray-400 placeholder:text-xs md:placeholder:text-sm transition-all duration-300 backdrop-blur-sm"
          autoComplete="off"
        />
        
        {/* כפתור נקה - מותאם למובייל */}
        {searchTerm && (
          <button
            type="button"
            onClick={clearSearch}
            className="absolute left-10 md:left-14 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500 transition-all duration-200 hover:scale-110"
          >
            <X className="h-3.5 w-3.5 md:h-4 md:w-4" />
          </button>
        )}
        
        {/* כפתור חיפוש - מותאם למובייל עם גרדיינט */}
        <button
          type="submit"
          disabled={loading}
          className="absolute left-1 md:left-2 top-1/2 transform -translate-y-1/2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 disabled:from-gray-400 disabled:to-gray-500 text-white p-1.5 md:p-2 rounded-xl transition-all duration-300 shadow-lg hover:shadow-cyan-500/50 disabled:shadow-none"
        >
          {loading ? (
            <Loader className="h-3.5 w-3.5 md:h-4 md:w-4 animate-spin" />
          ) : (
            <Search className="h-3.5 w-3.5 md:h-4 md:w-4" />
          )}
        </button>
      </form>

      {/* רשימת הצעות - באמצעות Portal במובייל */}
      {showSuggestions && dropdownPosition.width > 0 && (() => {
        const dropdown = (
          <div 
            ref={suggestionsRef}
            className="bg-white border border-gray-200 rounded-xl shadow-2xl max-h-96 overflow-y-auto"
            style={{
              position: 'fixed',
              top: dropdownPosition.top + 'px',
              left: dropdownPosition.left + 'px',
              width: dropdownPosition.width + 'px',
              maxWidth: 'calc(100vw - 2rem)',
              zIndex: 999999
            }}
          >
          {loadingSuggestions ? (
            <div className="flex items-center justify-center py-4">
              <Loader className="h-5 w-5 animate-spin text-blue-600" />
              <span className="mr-2 text-gray-600">מחפש...</span>
            </div>
          ) : suggestions.length > 0 ? (
            <>
         {suggestions.map((suggestion, index) => (
  <button
    key={`${suggestion.id}-${index}`}
    onClick={() => selectSuggestion(suggestion)}
    className={`w-full text-right px-3 md:px-4 py-2 md:py-3 hover:bg-blue-50 border-b border-gray-100 last:border-b-0 transition-colors ${
      selectedIndex === index ? 'bg-blue-50 border-blue-200' : ''
    }`}
  >
    <div className="flex items-center justify-between">
      <div className="flex-1 min-w-0">
        {/* שם המוצר */}
        <div className="flex items-center gap-2">
          <div className="font-medium text-gray-900 truncate text-sm md:text-base flex-1">
            {suggestion.name}
          </div>
          
          {/* אינדיקטור פעילות */}
          {suggestion.isActive === false && (
            <span className="bg-red-100 text-red-700 text-xs px-2 py-1 rounded-full font-medium border border-red-200">
              לא פעילה
            </span>
          )}
        </div>
        
        {/* פרטים נוספים - עם סוג המוצר */}
        <div className="flex items-center gap-1 md:gap-2 mt-1 text-xs md:text-sm text-gray-600">
          <span className="bg-gray-100 px-1 md:px-2 py-1 rounded text-xs">
           מספר באוצר : {suggestion.id}
          </span>
          
          {/* 🆕 סוג המוצר */}
          <span className="bg-blue-100 text-blue-700 px-1 md:px-2 py-1 rounded text-xs font-medium border border-blue-200">
            {getProductType(suggestion)}
          </span>
          
          <span className="truncate hidden sm:inline">{suggestion.company}</span>
        </div>
      </div>
      
      {/* תשואה */}
      <div className="mr-2 md:mr-4">
        <div className={`px-2 py-1 rounded-lg text-center ${
          (suggestion.returns?.year1 || 0) >= 0 
            ? 'bg-green-50 border border-green-200' 
            : 'bg-red-50 border border-red-200'
        }`}>
          <div className="text-xs text-gray-600">תשואה שנתית</div>
          <div className={`font-bold text-sm ${
            (suggestion.returns?.year1 || 0) >= 0 ? 'text-green-700' : 'text-red-700'
          }`}>
            {(suggestion.returns?.year1 || 0).toFixed(2)}%
          </div>
        </div>
      </div>
    </div>
  </button>
))}

              
              {/* הודעה בתחתית - מותאמת למובייל */}
              <div className="px-3 md:px-4 py-2 bg-gray-50 text-center text-xs text-gray-500">
                {suggestions.length} תוצאות • השתמש בחצים לניווט • Enter לבחירה
              </div>
            </>
          ) : searchTerm.length >= 2 ? (
            <div className="px-3 md:px-4 py-3 text-center text-gray-500 text-sm">
              לא נמצאו תוצאות עבור "{searchTerm}"
            </div>
          ) : null}
          </div>
        );
        
        return createPortal(dropdown, document.body);
      })()}


    </div>
  );
};

export default OptimizedSearchAutocomplete;